import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CreatePatientModal from './modals/patient/CreatePatientModal';
import CreateUserModal from './modals/user/CreateUserModal';
import { useQuickActions } from '../contexts/QuickActionsContext';
import { createPatient } from '../store/slices/patientsSlice';
import { createUser } from '../store/slices/usersSlice';
import type { AppDispatch } from '../store/store';
import type { CreatePatientRequest } from '../types/api';
import type { Api } from '../types/generated';

const GlobalModals: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    createPatientModalOpen, 
    createUserModalOpen, 
    closeCreatePatientModal, 
    closeCreateUserModal 
  } = useQuickActions();

  // Get loading states from Redux
  const patientsLoading = useSelector((state: any) => state.patients.loading);
  const usersLoading = useSelector((state: any) => state.users.loading);

  // Handle create patient - converts CreatePatientRequest to Api.Patient format
  const handleCreatePatient = async (patientData: CreatePatientRequest): Promise<{ id: string; href: string }> => {
    try {
      // Convert CreatePatientRequest to Api.Patient format expected by the thunk
      const apiPatientData: Api.Patient = {
        first_name: patientData.first_name,
        last_name: patientData.last_name,
        phone: patientData.phone,
        email: patientData.email,
        date_of_birth: patientData.date_of_birth,
        gender: patientData.gender,
        address: {
          street: patientData.address.street,
          city: patientData.address.city,
          state: patientData.address.state,
          postal_code: patientData.address.postal_code,
        }
      };
      
      const result = await dispatch(createPatient(apiPatientData)).unwrap();
      closeCreatePatientModal();
      
      // Return the expected format for CreatePatientModal
      return { id: result.id, href: result.href };
    } catch (error) {
      console.error('Failed to create patient:', error);
      throw error;
    }
  };

  // Handle create user
  const handleCreateUser = async (userData: any) => {
    try {
      await dispatch(createUser(userData)).unwrap();
      closeCreateUserModal();
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  };

  return (
    <>
      {/* Global Create Patient Modal */}
      <CreatePatientModal
        isOpen={createPatientModalOpen}
        onClose={closeCreatePatientModal}
        onCreate={handleCreatePatient}
        loading={patientsLoading}
      />

      {/* Global Create User Modal */}
      <CreateUserModal
        isOpen={createUserModalOpen}
        onClose={closeCreateUserModal}
        onCreate={handleCreateUser}
        loading={usersLoading}
      />
    </>
  );
};

export default GlobalModals;